"use client";

import { format } from "date-fns";
import { fi as fiLocale } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useRef } from "react";
import {
  LuCheck,
  LuChevronDown,
  LuChevronRight,
  LuTrophy,
} from "react-icons/lu";

import { useAuth } from "@/contexts/AuthContext";
import { useChatInbox } from "@/hooks/useChatInbox";
import { useNavigation } from "@/hooks/useNavigation";
import { useTournamentMatches } from "@/hooks/useTournamentMatches";
import { useUserTeamForTournament } from "@/hooks/useUserTeamForTournament";
import { Link } from "@/i18n/routing";
import {
  ARCHIVED_VISIBLE_SECTION_IDS,
  TOURNAMENT_NAV_SECTIONS,
  buildSectionHref,
} from "@/lib/navigation-config";
import type {
  MobileNavProps,
  NavChild,
  TournamentNavSection,
} from "@/types/navigation";

import { MobileHeaderBar } from "./MobileHeaderBar";
import { NavDrawer } from "./NavDrawer";

/**
 * Mobile navigation header for tournament pages.
 *
 * Row 1: hamburger (opens NavDrawer) + brand + notifications + avatar.
 * Row 2 (trigger): collapsed "tournament name › section › child" — tapping
 *   toggles the level-1/level-2 panel below.
 *
 * Level-2 chat children come from the unified chat inbox + scheduled match
 * times joined by matchId. Other L1 sections navigate directly.
 */
export function MobileNav({
  tournamentName,
  tournamentSlug,
  tournamentId,
  archived,
}: MobileNavProps) {
  const t = useTranslations();
  const locale = useLocale();
  const nav = useNavigation();
  const { user } = useAuth();

  const { data: userTeam } = useUserTeamForTournament(tournamentId);
  const { data: matches = [] } = useTournamentMatches(tournamentId);
  const { groups } = useChatInbox(user?.id);

  // Chat children: tournament + (team if user has team) + per-match
  const keskusteluChildren = useMemo<NavChild[]>(() => {
    const dateLocale = locale === "fi" ? fiLocale : undefined;
    const tournamentEntry = groups.tournament.find(
      (e) => e.tournamentId === tournamentId,
    );
    const teamEntry = groups.team.find((e) => e.tournamentId === tournamentId);
    const matchEntries = groups.match
      .filter((e) => e.tournamentId === tournamentId)
      .slice()
      .sort((a, b) => {
        // Chronological by scheduled match time; fall back to lastMessageAt.
        const ma = matches.find((m) => m.id === a.chatId);
        const mb = matches.find((m) => m.id === b.chatId);
        const at = ma?.matchDate?.getTime() ?? a.lastMessageAt?.getTime() ?? 0;
        const bt = mb?.matchDate?.getTime() ?? b.lastMessageAt?.getTime() ?? 0;
        return at - bt;
      });

    const out: NavChild[] = [];

    out.push({
      id: "tournament",
      href: `/tournaments/${tournamentSlug}/chat`,
      label: t("nav.chat.tournament"),
      type: "general",
      unreadCount: tournamentEntry?.unreadCount ?? 0,
    });

    if (userTeam) {
      out.push({
        id: "team",
        href: `/tournaments/${tournamentSlug}/chat/team`,
        label: userTeam.name
          ? t("nav.chat.team", { teamName: userTeam.name })
          : t("nav.chat.teamFallback"),
        type: "general",
        unreadCount: teamEntry?.unreadCount ?? 0,
      });
    }

    for (const entry of matchEntries) {
      const opponentName =
        userTeam?.id && entry.team1Id === userTeam.id
          ? entry.team2Name
          : entry.team1Name ?? entry.team2Name;
      const matchData = matches.find((m) => m.id === entry.chatId);
      const meta = matchData?.matchDate
        ? format(matchData.matchDate, "d.M. 'klo' HH:mm", {
            locale: dateLocale,
          })
        : t("nav.chat.matchNoTime");
      out.push({
        id: entry.chatId,
        href: `/tournaments/${tournamentSlug}/chat/match/${entry.chatId}`,
        label:
          entry.team1Name && entry.team2Name
            ? `${entry.team1Name} × ${entry.team2Name}`
            : opponentName ?? t("nav.chat.matchNoTime"),
        meta,
        type: "match",
        unreadCount: entry.unreadCount ?? 0,
      });
    }

    return out;
  }, [groups, matches, userTeam, tournamentId, tournamentSlug, t, locale]);

  const keskusteluUnread = keskusteluChildren.reduce(
    (sum, c) => sum + (c.unreadCount ?? 0),
    0,
  );

  // Trigger row labels: current section + (chat-only) current chat
  const activeL1Section = TOURNAMENT_NAV_SECTIONS.find(
    (s) => s.id === nav.activeL1,
  );
  const activeL2Child =
    nav.activeL1 === "chat" && nav.activeL2
      ? keskusteluChildren.find((c) => c.id === nav.activeL2)
      : null;

  // Publish the collapsed row1+row2 height as `--mobile-nav-h` so layout
  // spacers and chat height math stay accurate. The L2 panel is rendered
  // as an absolute overlay below the trigger row, so opening it does NOT
  // push page content down.
  const triggerStackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = triggerStackRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      document.documentElement.style.setProperty(
        "--mobile-nav-h",
        `${entry.contentRect.height}px`,
      );
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--mobile-nav-h");
    };
  }, []);

  return (
    <>
      <NavDrawer isOpen={nav.isDrawerOpen} onClose={nav.closeDrawer} />

      <header
        className="fixed inset-x-0 top-0 z-50 bg-tn-bg-nav lg:hidden"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* Top gold accent line */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(250,204,21,0.4), transparent)",
          }}
        />

        <div ref={triggerStackRef} className="bg-tn-bg-nav">
        <MobileHeaderBar
          tournamentSlug={tournamentSlug}
          onOpenDrawer={nav.openDrawer}
        />

        {/* Row 2: trigger */}
        <button
          type="button"
          onClick={nav.toggleMenu}
          aria-expanded={nav.isMenuOpen}
          aria-controls="mobile-nav-menu"
          aria-label={t("nav.aria.openTournamentMenu")}
          className={cx(
            "flex w-full items-center justify-between gap-3 border-t border-tn-border px-4 py-2.5 text-left transition-colors",
            nav.isMenuOpen && "bg-tn-elevated",
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="font-display flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-tn-gold/85">
              <LuTrophy size={11} />
              <span className="truncate">{tournamentName}</span>
            </div>
            <div className="font-display flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[13px] font-semibold uppercase tracking-[0.04em] text-tn-text-primary">
              <span>
                {activeL1Section ? t(activeL1Section.labelKey) : ""}
              </span>
              {activeL2Child && (
                <>
                  <span className="font-normal text-tn-text-muted">›</span>
                  <span className="overflow-hidden text-ellipsis text-tn-gold">
                    {shortMatchLabel(activeL2Child.label)}
                  </span>
                </>
              )}
            </div>
          </div>
          <span
            aria-hidden="true"
            className={cx(
              "flex size-8 shrink-0 items-center justify-center rounded-md border border-tn-gold/15 bg-tn-gold/10 text-tn-gold transition-transform",
              nav.isMenuOpen && "rotate-180 bg-tn-gold/15",
            )}
          >
            <LuChevronDown size={14} strokeWidth={2.5} />
          </span>
        </button>
        </div>

        {/* Level-1/2 panel — absolutely positioned so it overlays page
            content instead of pushing it when opened. */}
        <div
          id="mobile-nav-menu"
          className={cx(
            "absolute left-0 right-0 top-full overflow-y-auto bg-tn-bg-panel transition-[max-height] duration-[280ms] ease-in-out",
            nav.isMenuOpen
              ? "max-h-[70vh] border-b border-tn-border-strong"
              : "max-h-0",
          )}
        >
          <div className="flex items-center gap-2 px-5 pb-2 pt-3">
            <LuTrophy size={13} className="text-tn-gold/70" />
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-tn-text-muted">
              {t("nav.menuLabel")}
            </span>
            <span className="ml-1 h-px flex-1 bg-gradient-to-r from-tn-border to-transparent" />
          </div>

          {TOURNAMENT_NAV_SECTIONS.filter(
            (section) =>
              !archived || ARCHIVED_VISIBLE_SECTION_IDS.includes(section.id),
          ).map((section) => (
            <NavL1
              key={section.id}
              section={section}
              tournamentSlug={tournamentSlug}
              isActive={nav.activeL1 === section.id}
              isExpanded={nav.isExpanded(section.id)}
              unreadCount={
                section.id === "chat" ? keskusteluUnread : 0
              }
              l2Children={section.id === "chat" ? keskusteluChildren : []}
              activeL2={nav.activeL2}
              onExpandToggle={() => nav.toggleExpand(section.id)}
              t={t}
            />
          ))}
        </div>
      </header>
    </>
  );
}

type NavL1Props = {
  section: TournamentNavSection;
  tournamentSlug: string;
  isActive: boolean;
  isExpanded: boolean;
  unreadCount: number;
  l2Children: NavChild[];
  activeL2: string | null;
  onExpandToggle: () => void;
  t: (key: string) => string;
};

function NavL1({
  section,
  tournamentSlug,
  isActive,
  isExpanded,
  unreadCount,
  l2Children,
  activeL2,
  onExpandToggle,
  t,
}: NavL1Props) {
  const hasChildren = l2Children.length > 0;
  const sectionHref = buildSectionHref(section.hrefTemplate, tournamentSlug);

  const inner = (
    <>
      <span>{t(section.labelKey)}</span>
      <span className="flex items-center gap-2">
        {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
        {hasChildren && (
          <span
            aria-hidden="true"
            className={cx(
              "flex transition-transform",
              isExpanded ? "rotate-90 opacity-100" : "opacity-50",
            )}
          >
            <LuChevronRight size={14} strokeWidth={2.5} />
          </span>
        )}
      </span>
    </>
  );

  return (
    <>
      {hasChildren ? (
        <button
          type="button"
          onClick={onExpandToggle}
          aria-expanded={isExpanded}
          className={cx(
            "font-display flex w-full items-center justify-between gap-3 border-l-[3px] border-transparent px-5 py-3 text-left text-[13px] font-semibold uppercase tracking-[0.1em] transition-colors",
            isActive || isExpanded
              ? "border-tn-gold bg-tn-gold/5 text-tn-gold"
              : "text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary",
          )}
        >
          {inner}
        </button>
      ) : (
        <Link
          href={sectionHref}
          className={cx(
            "font-display flex w-full items-center justify-between gap-3 border-l-[3px] border-transparent px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.1em] transition-colors",
            isActive
              ? "border-tn-gold bg-tn-gold/5 text-tn-gold"
              : "text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary",
          )}
        >
          {inner}
        </Link>
      )}

      {hasChildren && (
        <div
          className={cx(
            "overflow-hidden bg-black/20 transition-[max-height] duration-[280ms] ease-in-out",
            isExpanded
              ? "max-h-[800px] border-b border-tn-border"
              : "max-h-0",
          )}
        >
          {renderChildren(l2Children, activeL2, t)}
        </div>
      )}
    </>
  );
}

/**
 * Renders L2 children with a "Match chats" divider between the last general
 * entry and the first match entry.
 */
function renderChildren(
  children: NavChild[],
  activeL2: string | null,
  t: (key: string) => string,
) {
  const nodes: React.ReactNode[] = [];
  let lastType: NavChild["type"] | null = null;

  for (const child of children) {
    if (lastType === "general" && child.type === "match") {
      nodes.push(
        <div
          key={`__divider-${child.id}`}
          className="font-display flex items-center gap-2 px-5 pb-1.5 pl-11 pt-3 text-[9px] font-semibold uppercase tracking-[0.18em] text-tn-text-muted"
        >
          {t("nav.chat.matchChatsDivider")}
          <span className="h-px flex-1 bg-gradient-to-r from-tn-border to-transparent" />
        </div>,
      );
    }
    lastType = child.type;

    const isActive = activeL2 === child.id;
    nodes.push(
      <Link
        key={child.id}
        href={child.href}
        className={cx(
          "font-body flex items-center justify-between gap-2.5 border-l-[3px] border-transparent py-2.5 pl-11 pr-5 text-[13px] font-medium transition-colors",
          isActive
            ? "border-tn-gold bg-tn-gold/[0.08] font-semibold text-tn-gold"
            : "text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary",
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {child.label}
          </div>
          {child.meta && (
            <div className="mt-px text-[10.5px] font-normal tracking-normal text-tn-text-muted">
              {child.meta}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {isActive ? (
            <span className="flex size-4 items-center justify-center text-tn-gold">
              <LuCheck size={14} strokeWidth={3} />
            </span>
          ) : (
            child.unreadCount &&
            child.unreadCount > 0 && <Badge>{child.unreadCount}</Badge>
          )}
        </div>
      </Link>,
    );
  }

  return nodes;
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] bg-tn-purple px-1.5 text-[10px] font-bold leading-none text-white">
      {children}
    </span>
  );
}

/** "Sigma Stars × Frost Giants" → "Sigma × Frost" — keeps trigger row tidy. */
function shortMatchLabel(label: string): string {
  const parts = label.split(" × ");
  if (parts.length !== 2) return label;
  const a = parts[0].split(" ")[0];
  const b = parts[1].split(" ")[0];
  return `${a} × ${b}`;
}

function cx(
  ...parts: Array<string | false | null | undefined>
): string {
  return parts.filter(Boolean).join(" ");
}
